import React from 'react';
import parse from 'html-react-parser';
import { toast } from 'react-toastify';

import Quiz from './Quiz';
import services from 'api/services';
import { FreecodePlayer } from 'components/FreecodePlayer/freecodePlayer';
import { executorDecompress } from 'functions/executorDecompress';
import { OutlineButton } from 'components/Button/RoundButton';
import { InteractContainer } from './styles';
import { getNewPixiComponent } from 'components/FreecodePlayer/pixiComponent';
const { XPService } = services;

const hasIdPresentInChildProps = (element, targetId) => {
  if (element?.props?.id === targetId) return true;
  if (element?.props?.children) {
    if (Array.isArray(element.props.children)) {
      for (const child of element.props.children) {
        if (hasIdPresentInChildProps(child, targetId)) {
          return true;
        }
      }
    } else if (hasIdPresentInChildProps(element.props.children, targetId)) {
      return true;
    }
  }
  return false;
};

const replacePTagWithDivTag = (transformedData, targetId) => {
  let dataToBeTransform = transformedData.length ? transformedData : [transformedData];
  return dataToBeTransform?.map((element) => {
    if (element?.type === 'p' && hasIdPresentInChildProps(element, targetId)) {
      return React.createElement('div', element.props, element.props.children);
    }
    return element;
  });
};

const getPeekData = (testImpl) => {
  if (!testImpl?.generated?.peekResponse) return;
  let data = executorDecompress(testImpl.generated.peekResponse);
  if (!data?.length) return;
  return data.slice(0, testImpl.configLevels.numPublic + testImpl.configLevels.numHidden);
};

const awardXP = async (userId, type, isCreator) => {
  try {
    if (isCreator || !userId) return;
    let response = await XPService.updateCourseFlowEvent({ type });
    toast.success(response.description);
  } catch (e) {
    toast.error(typeof e === 'string' ? e : e?.message || 'Could not award XP');
  }
};

export const codingButtonParser = (text, exercise, course, isCreator, navigate, close) => {
  let transformedData = parse(text, {
    transform: (reactNode) => {
      if (typeof reactNode === 'string' && reactNode.includes('[coding]')) {
        const regex = /\[coding\](.*?)\[\/coding\]/;
        const match = regex.exec(text);
        let extractedText = 'Continue';
        if (match) extractedText = match[1];

        return (
          <OutlineButton
            id="coding"
            small
            secondaryBlue
            removeMargin
            onClick={() => {
              if (isCreator || !course) return close();
              if (course) {
                navigate(`/exercise/${exercise}?courseId=${course}`);
                return close();
              }
            }}
          >
            {extractedText}
          </OutlineButton>
        );
      }
      return reactNode;
    },
  });
  return { data: replacePTagWithDivTag(transformedData, 'coding'), type: 'customTag' };
};

export const quizParser = (
  text,
  subLevelIndex,
  userLevel,
  exercise,
  isCreator,
  setHasCompletedInteraction,
  userId,
  testImplId,
  language,
  updateFlowProgress,
  inputExercise
) => {
  let isInsideQuizTag = false;
  setHasCompletedInteraction(false);

  let transformedData = parse(text, {
    transform: (reactNode) => {
      if (typeof reactNode === 'string') {
        if (reactNode.includes('[quiz]')) {
          isInsideQuizTag = true;
          return (
            <Quiz
              id="quiz"
              key={text}
              quizText={text}
              userLevel={userLevel}
              subLevel={subLevelIndex}
              exercise={exercise}
              isCreator={isCreator}
              testImplId={testImplId}
              language={language}
              updateFlowProgress={updateFlowProgress}
              inputExercise={inputExercise}
              setQuizCompleted={(value, awardXPToUser) => {
                setHasCompletedInteraction(value);
                if (awardXPToUser) awardXP(userId, 'quiz', isCreator);
              }}
            />
          );
        } else if (reactNode.includes('[/quiz]')) {
          isInsideQuizTag = false;
          return null;
        }
        return reactNode;
      } else if (hasIdPresentInChildProps(reactNode, 'quiz')) {
        return reactNode;
      } else if (reactNode.type === 'p') {
        return !isInsideQuizTag ? reactNode : null;
      } else {
        return reactNode;
      }
    },
  });

  transformedData = transformedData.filter((data) => data);
  return { data: replacePTagWithDivTag(transformedData, 'quiz'), type: 'customTag' };
};

const getPixiComponent = (key) => {
  if (!window.customPixi) window.customPixi = {};
  if (!window.customPixi[key]) window.customPixi[key] = getNewPixiComponent();
  return window.customPixi[key];
};

export const interactParser = (
  text,
  subLevelIndex,
  currentLevelIndex,
  userLevel,
  exercise,
  isCreator,
  testImpl,
  userId,
  language,
  updateFlowProgress,
  inputExercise,
  setHasCompletedInteraction
) => {
  const transformedData = parse(text, {
    transform: (reactNode) => {
      if (typeof reactNode === 'string') {
        if (reactNode.includes('[interact-')) {
          const regex = /interact-(\d+)/;
          const match = text.match(regex)[1];

          if (getPeekData(testImpl).length < match) return <>Interact not found for level {match}</>;

          const key = `canvas${userLevel}${subLevelIndex}${language}`;
          if (window.customPixiComponentForFlow?.[key]) {
            return window.customPixiComponentForFlow[key];
          } else {
            window.customPixiComponentForFlow = { ...(window.customPixiComponentForFlow ?? {}), [key]: false };
          }

          const component = (
            <InteractContainer>
              <FreecodePlayer
                key={key}
                id="interact"
                testImplementation={testImpl}
                levels={getPeekData(testImpl)}
                levelToRun={match - 1}
                sizeLimitByParent
                isFlowTab
                PixiComponent={getPixiComponent(key)}
                isActiveInteract={subLevelIndex === currentLevelIndex}
                humanInputProps={{
                  code: testImpl?.solution || testImpl?.generated?.skeletons?.python,
                  language: 'python',
                  testId: testImpl?.test,
                  testImplId: testImpl?._id,
                }}
                updateInteractStatus={(value) => {
                  setHasCompletedInteraction(value);
                }}
                updateInteractResult={(awardXPToUser) => {
                  if (isCreator || !userId) return;
                  let flowProgress = JSON.parse(
                    JSON.stringify(inputExercise?.levelData?.[userLevel]?.[language]?.flowProgress)
                  );
                  if (awardXPToUser && !flowProgress[subLevelIndex].isAwarded) {
                    window.customPixiComponentForFlow[key] = component;
                    flowProgress[subLevelIndex].isAwarded = true;
                    updateFlowProgress({
                      test: exercise,
                      language: language,
                      level: userLevel,
                      data: flowProgress,
                    });
                    awardXP(userId, 'interact', isCreator);
                  }
                }}
              />
            </InteractContainer>
          );
          return component;
        }
      }
      return reactNode;
    },
  });

  return { data: replacePTagWithDivTag(transformedData, 'interact'), type: 'customTag' };
};
